import SOURCE_TYPE from '../SourceCreate.constants';
import { useSourceCreateState } from '../SourceCreate.hooks';
import {
  useFetchLimaDevices,
  useFetchMicrosoftDataProviderCategories,
  useFetchSourceDetailBySourceUID,
  useFetchSourceLogType,
  useFetchSourceTypeBasic,
  useFetchSourceTypeSchema,
  useFetchSourceTypesByTier
} from '../SourceCreate.service';
import useCategoryValidations from './useCategoryValidations.hooks';

/**
 * Manages all data fetching queries for the source form.
 *
 * Responsibilities:
 * - Fetch source types based on organization tier
 * - Fetch source type schemas
 * - Fetch categories/log types
 * - Fetch Lima devices
 * - Fetch source details for editing
 * - Fetch Microsoft data provider categories
 * - Run category validations
 *
 * @param mode - Dialog mode (New or Edit)
 * @returns All query results needed by the form
 */
export const useSourceFormQueries = (
  mode: string,
  isCustomerCloudSourcePage?: boolean
) => {
  const { selectedSourceTypeId, selectedCloudSource } = useSourceCreateState();

  // Fetch the source type basic data for source type dropdown
  const sourceTypeBasicQuery = useFetchSourceTypeBasic(
    isCustomerCloudSourcePage
  );

  // Fetch the source types based on tier
  useFetchSourceTypesByTier();

  // Fetch the source type schema based on the selected source type
  const sourceTypeSchemaQuery = useFetchSourceTypeSchema(mode);

  // Fetch category list based on the selected source type
  const sourceLogTypeQuery = useFetchSourceLogType(selectedSourceTypeId);

  // Fetch all lima devices
  const limaDevicesQuery = useFetchLimaDevices();

  // Fetch source details based on the selected source
  const sourceDetailQuery = useFetchSourceDetailBySourceUID(
    selectedCloudSource?.source_uid ?? '',
    mode
  );

  // Fetch Microsoft dataproviders categories
  const isMicrosoftSelected = selectedSourceTypeId === SOURCE_TYPE.MICROSOFT;
  useFetchMicrosoftDataProviderCategories(
    [
      SOURCE_TYPE.O365,
      SOURCE_TYPE.MICROSOFT_DEFENDER,
      SOURCE_TYPE.MICROSOFT_ENTRA_ID
    ],
    isMicrosoftSelected
  );

  // Run category validations
  useCategoryValidations();

  return {
    sourceTypeBasicQuery,
    sourceTypeSchemaQuery,
    sourceLogTypeQuery,
    limaDevicesQuery,
    sourceDetailQuery
  };
};
